import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Card, Button, Badge, Form, InputGroup, Dropdown, Spinner } from 'react-bootstrap';
import { KTCard, KTCardBody, KTIcon } from '../../../../_metronic/helpers';
import { Asset, AssetCategory, AssetFilter, LicenseSource } from '../types/assetTypes';
import { get_filtered_assets, delete_asset } from '../api/assetsApi';
import { get_all_categories } from '../../projects/api/projectCategoriesApi';
import { get_all_platforms } from '../../platforms/api/platformsApi';
import { toast } from 'react-toastify';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import AssetDownloadModal from './AssetDownloadModal';

interface CategoryItem {
  id: number;
  name: string;
}

interface PlatformItem {
  id: number;
  platform_name: string;
}

const AssetsList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{
    upload_name?: string;
    category_flag?: 'specific' | 'all';
    category_id?: number;
    platform_flag?: 'specific' | 'all';
    platform_id?: number;
    page?: number;
    per_page?: number;
  }>({
    category_flag: 'all',
    platform_flag: 'all',
    page: 1,
    per_page: 36,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false);
  const queryClient = useQueryClient();

  // Permission checks
  const canViewAssets = Boolean(permissionCheck('upload-view'));
  const canCreateAsset = Boolean(permissionCheck('upload-create'));
  const canUpdateAsset = Boolean(permissionCheck('upload-update'));
  const canDeleteAsset = Boolean(permissionCheck('upload-delete'));
  const canDownloadAsset = Boolean(permissionCheck('download-view'));

  // Load categories and platforms
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const res = await get_all_categories();
        const apiCats = res?.data?.all_categories || res?.data?.categories || res?.data || [];
        const all: CategoryItem[] = (Array.isArray(apiCats) ? apiCats : []).map((c: any) => ({
          id: Number(c.id),
          name: c.category_name || c.name || '',
        }));
        setCategories(all);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error('Failed to load categories');
      } finally {
        setIsLoadingCategories(false);
      }
    };

    const loadPlatforms = async () => {
      try {
        setIsLoadingPlatforms(true);
        const res = await get_all_platforms();
        const apiPlatforms = res?.data?.all_platforms || res?.data?.platforms || res?.data || [];
        const all: PlatformItem[] = (Array.isArray(apiPlatforms) ? apiPlatforms : []).map((p: any) => ({
          id: Number(p.id),
          platform_name: p.platform_name || p.name || '',
        }));
        setPlatforms(all);
      } catch (error) {
        console.error('Error loading platforms:', error);
        toast.error('Failed to load platforms');
      } finally {
        setIsLoadingPlatforms(false);
      }
    };

    loadCategories();
    loadPlatforms();
  }, []);

  const { data, isLoading, error, refetch } = useQuery(
    ['filtered_assets', filters, searchTerm],
    () => get_filtered_assets({
      upload_name: searchTerm || undefined,
      category_flag: filters.category_flag || 'all',
      category_id: filters.category_flag === 'specific' ? filters.category_id : undefined,
      platform_flag: filters.platform_flag || 'all',
      platform_id: filters.platform_flag === 'specific' ? filters.platform_id : undefined,
      page: filters.page || 1,
      per_page: filters.per_page || 36,
    }),
    {
      enabled: true,
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation(
    (id: number) => delete_asset(id),
    {
      onSuccess: () => {
        toast.success('Asset deleted successfully');
        queryClient.invalidateQueries(['assets']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete asset');
      },
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset to page 1 when search term changes
      setFilters(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleCategoryChange = (categoryId: string) => {
    setFilters(prev => ({
      ...prev,
      category_flag: categoryId ? 'specific' : 'all',
      category_id: categoryId ? Number(categoryId) : undefined,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handlePlatformChange = (platformId: string) => {
    setFilters(prev => ({
      ...prev,
      platform_flag: platformId ? 'specific' : 'all',
      platform_id: platformId ? Number(platformId) : undefined,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setFilters(prev => ({ ...prev, per_page: perPage, page: 1 })); // Reset to page 1 when changing per page
  };


  const handleView = (asset: Asset) => {
    if (asset.id) {
      navigate(`/digital-assets/view/${asset.id}`);
    }
  };

  const handleDownload = (asset: Asset) => {
    // Check if asset file exists
    if (!asset.zip_file_url || asset.zip_file_url === null || asset.zip_file_url.trim() === '') {
      toast.warning('File is not uploaded yet. Please upload the file first before downloading.');
      return;
    }
    setSelectedAsset(asset);
    setShowDownloadModal(true);
  };


  const handleEdit = (asset: Asset) => {
    if (asset.id) {
      navigate(`/digital-assets/upload/${asset.id}`);
    }
  };

  const handleDelete = (asset: Asset) => {
    if (window.confirm(`Are you sure you want to delete "${asset.name}"?`)) {
      if (asset.id) {
        deleteMutation.mutate(asset.id);
      }
    }
  };

  const getCategoryLabel = (category: AssetCategory, categoryName?: string): string => {
    // Use API category name if available, otherwise convert enum to label
    if (categoryName) {
      return categoryName;
    }
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getCategoryBadgeVariant = (category: AssetCategory, categoryName?: string): string => {
    // Determine variant based on category name from API if available
    if (categoryName) {
      const name = categoryName.toLowerCase();
      if (name.includes('video template')) return 'primary';
      if (name.includes('stock video') || name.includes('videos')) return 'success';
      if (name.includes('audio')) return 'warning';
      return 'info';
    }
    // Fallback to enum-based variants
    const variants: Record<AssetCategory, string> = {
      [AssetCategory.VIDEO_TEMPLATES]: 'primary',
      [AssetCategory.STOCK_VIDEOS]: 'success',
      [AssetCategory.AUDIOS]: 'warning',
      [AssetCategory.GRAPHICS]: 'info',
    };
    return variants[category] || 'secondary';
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user has view permission
  if (!canViewAssets) {
    return (
      <KTCard>
        <KTCardBody>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view assets.
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  const assets: Asset[] = data?.assets || [];
  const defaultPagination = {
    current_page: 1,
    per_page: 12,
    total: 0,
    total_pages: 1,
    from: 0,
    to: 0,
    last_page: 1,
    first_page_url: '',
    last_page_url: '',
    next_page_url: null,
    prev_page_url: null,
    path: '',
    links: [],
  };
  
  const pagination = data?.pagination ? {
    current_page: data.pagination.current_page ?? defaultPagination.current_page,
    per_page: data.pagination.per_page ?? defaultPagination.per_page,
    total: data.pagination.total ?? defaultPagination.total,
    total_pages: data.pagination.total_pages ?? defaultPagination.total_pages,
    from: (data.pagination as any).from ?? defaultPagination.from,
    to: (data.pagination as any).to ?? defaultPagination.to,
    last_page: (data.pagination as any).last_page ?? defaultPagination.last_page,
    first_page_url: (data.pagination as any).first_page_url ?? defaultPagination.first_page_url,
    last_page_url: (data.pagination as any).last_page_url ?? defaultPagination.last_page_url,
    next_page_url: (data.pagination as any).next_page_url ?? defaultPagination.next_page_url,
    prev_page_url: (data.pagination as any).prev_page_url ?? defaultPagination.prev_page_url,
    path: (data.pagination as any).path ?? defaultPagination.path,
    links: (data.pagination as any).links ?? defaultPagination.links,
  } : defaultPagination;

  return (
    <>
      <KTCard>
        <KTCardBody>
          <div className="d-flex justify-content-between align-items-center mb-5">
            <div>
              <h2 className="mb-1">Digital Assets</h2>
              <p className="text-muted mb-0 small">Manage and organize your digital assets</p>
            </div>
            {canCreateAsset && (
              <Button variant="primary" onClick={() => navigate('/digital-assets/upload')}>
                <KTIcon iconName="plus" className="me-2" />
                Upload Asset
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="row g-3 mb-5">
            {/* Search */}
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Text>
                  <KTIcon iconName="magnifier" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
            
            {/* Category Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.category_id || ''}
                onChange={(e) => handleCategoryChange(e.target.value)}
                disabled={isLoadingCategories}
                className="form-select-sm"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Platform Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.platform_id || ''}
                onChange={(e) => handlePlatformChange(e.target.value)}
                disabled={isLoadingPlatforms}
                className="form-select-sm"
              >
                <option value="">All Platforms</option>
                {platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.platform_name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Reset Button */}
            <div className="col-md-3">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setFilters({
                    category_flag: 'all',
                    platform_flag: 'all',
                    page: 1,
                  });
                  setSearchTerm('');
                }}
                className="w-100"
                size="sm"
              >
                Reset
              </Button>
            </div>
          </div>

          {/* Assets Grid */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-3">Loading assets...</div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              Failed to load assets. Please try again.
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-5">
              <KTIcon iconName="file" className="fs-3x text-muted mb-3" />
              <div className="text-muted">No assets found</div>
            </div>
          ) : (
            <>
              <div className="row g-4">
                {assets.map((asset) => (
                  <div key={asset.id} className="col-md-3 col-lg-3">
                    <Card 
                      className="h-100 shadow-sm border-0 card-hover" 
                      style={{ transition: 'all 0.3s ease', cursor: canViewAssets ? 'pointer' : 'default' }}
                      onClick={canViewAssets ? () => handleView(asset) : undefined}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div className="position-relative">
                        {asset.thumbnails && asset.thumbnails.length > 0 ? (
                          <Card.Img
                            variant="top"
                            src={asset.thumbnails[0]}
                            style={{ height: '220px', objectFit: 'cover' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e0e0e0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" font-size="18" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <div 
                            className="d-flex align-items-center justify-content-center bg-light"
                            style={{ height: '220px' }}
                          >
                            <KTIcon iconName="picture" className="fs-1 text-muted" />
                          </div>
                        )}
                        {/* Status Badge */}
                        <div className="position-absolute top-0 end-0 m-2">
                          {asset.zip_file_url && asset.zip_file_url !== null && asset.zip_file_url.trim() !== '' ? (
                            <Badge bg="success" className="px-2 py-1">
                              <KTIcon iconName="check-circle" className="me-1 fs-7" />
                              Ready
                            </Badge>
                          ) : (
                            <Badge bg="warning" className="px-2 py-1">
                              <KTIcon iconName="clock" className="me-1 fs-7" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        {/* Category Badge */}
                        {asset.category && (
                          <div className="position-absolute top-0 start-0 m-2">
                            <Badge bg={getCategoryBadgeVariant(asset.category, asset.category_name || asset.category_object?.category_name)} className="px-2 py-1">
                              {getCategoryLabel(asset.category, asset.category_name || asset.category_object?.category_name)}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <Card.Body className="d-flex flex-column p-3">
                        <Card.Title className="fs-6 fw-bold mb-2 text-truncate" title={asset.name}>
                          {asset.name}
                        </Card.Title>
                        
                        {/* Description Preview */}
                        {asset.description && (
                          <Card.Text className="text-muted small mb-2" style={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '40px'
                          }}>
                            {asset.description}
                          </Card.Text>
                        )}

                        {/* Platform Badge */}
                        {(asset.source_platform || asset.platform_object?.platform_name) && (
                          <div className="mb-2">
                            <Badge bg="info" className="px-2 py-1">
                              <KTIcon iconName="link" className="me-1 fs-8" />
                              {typeof asset.source_platform === 'string' ? asset.source_platform : (asset.platform_object?.platform_name || asset.source_platform)}
                            </Badge>
                          </div>
                        )}

                        {/* Metadata Info */}
                        <div className="mb-2">
                          <div className="d-flex flex-wrap gap-2 text-muted small">
                            {asset.metadata.size !== undefined && asset.metadata.size > 0 && (
                              <div className="d-flex align-items-center">
                                <KTIcon iconName="file" className="me-1 fs-7" />
                                <span>{formatBytes(asset.metadata.size)}</span>
                              </div>
                            )}
                            {asset.download_count !== undefined && asset.download_count !== null && (
                              <div className="d-flex align-items-center">
                                <KTIcon iconName="file-down" className="me-1 fs-7" />
                                <span>{asset.download_count}</span>
                              </div>
                            )}
                            <div className="d-flex align-items-center">
                              <KTIcon iconName="calendar" className="me-1 fs-7" />
                              <span>{formatDate(asset.uploaded_at)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Tags */}
                        {asset.tags.length > 0 && (
                          <div className="mb-2">
                            {asset.tags.slice(0, 2).map((tag, index) => (
                              <Badge key={index} bg="secondary" className="me-1 mb-1" style={{ fontSize: '10px' }}>
                                {tag}
                              </Badge>
                            ))}
                            {asset.tags.length > 2 && (
                              <Badge bg="secondary" style={{ fontSize: '10px' }}>
                                +{asset.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex gap-1 mt-auto pt-2 border-top" onClick={(e) => e.stopPropagation()}>
                          {canViewAssets && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(asset);
                              }}
                              className="flex-fill"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            >
                              <KTIcon iconName="eye" className="me-1 fs-7" />
                              View
                            </Button>
                          )}
                          {/* Show Edit button if file is not uploaded, otherwise show Download buttons */}
                          {(!asset.zip_file_url || asset.zip_file_url === null || asset.zip_file_url.trim() === '') ? (
                            canUpdateAsset && (
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(asset);
                                }}
                                className="flex-fill"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              >
                                <KTIcon iconName="pencil" className="me-1 fs-7" />
                                Edit
                              </Button>
                            )
                          ) : (
                            <>
                              {canDownloadAsset && (
                                <Button
                                  variant="info"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(asset);
                                  }}
                                  title="Download with license and association"
                                  className="flex-fill"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                  <KTIcon iconName="file-down" className="me-1 fs-7" />
                                  Download
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>

              {/* Pagination controls */}
              {pagination && pagination.total > 0 && (
                <div className='d-flex align-items-center justify-content-between mt-4 py-4 px-4'>
                  <div className='d-flex align-items-center gap-3'>
                    <span className='text-muted fs-7'>
                      Showing {pagination.from || 0}-{pagination.to || assets.length} of {pagination.total || assets.length}
                    </span>
                    <div className='d-flex align-items-center'>
                      <span className='me-2 text-muted fs-7'>Show</span>
                      <select
                        className='form-select form-select-sm w-auto'
                        value={filters.per_page || 36}
                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                        disabled={isLoading}
                      >
                        <option value={12}>12</option>
                        <option value={24}>24</option>
                        <option value={36}>36</option>
                        <option value={48}>48</option>
                        <option value={96}>96</option>
                      </select>
                      <span className='ms-2 text-muted fs-7'>per page</span>
                    </div>
                  </div>
                  <ul className='pagination mb-0'>
                    <li className={`page-item ${(pagination.current_page || 1) === 1 ? 'disabled' : ''}`}>
                      <button 
                        className='page-link' 
                        onClick={() => handlePageChange(1)}
                        disabled={(pagination.current_page || 1) === 1}
                      >
                        «
                      </button>
                    </li>
                    <li className={`page-item ${(pagination.current_page || 1) === 1 ? 'disabled' : ''}`}>
                      <button 
                        className='page-link' 
                        onClick={() => handlePageChange(Math.max(1, (pagination.current_page || 1) - 1))}
                        disabled={(pagination.current_page || 1) === 1}
                      >
                        Prev
                      </button>
                    </li>
                    <li className='page-item active'>
                      <span className='page-link'>{pagination.current_page || 1} / {pagination.total_pages || 1}</span>
                    </li>
                    <li className={`page-item ${(pagination.current_page || 1) === (pagination.total_pages || 1) ? 'disabled' : ''}`}>
                      <button 
                        className='page-link' 
                        onClick={() => handlePageChange(Math.min(pagination.total_pages || 1, (pagination.current_page || 1) + 1))}
                        disabled={(pagination.current_page || 1) === (pagination.total_pages || 1)}
                      >
                        Next
                      </button>
                    </li>
                    <li className={`page-item ${(pagination.current_page || 1) === (pagination.total_pages || 1) ? 'disabled' : ''}`}>
                      <button 
                        className='page-link' 
                        onClick={() => handlePageChange(pagination.total_pages || 1)}
                        disabled={(pagination.current_page || 1) === (pagination.total_pages || 1)}
                      >
                        »
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </KTCardBody>
      </KTCard>

      <AssetDownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
      />
    </>
  );
};

export default AssetsList;

